/**
 * One tab's worth of institution services, priced for this patient.
 *
 * The point of this screen is comparison. The same blood test is 400 at one
 * centre and 550 at another, and before institutions there was no way for a
 * patient to know that — so the card leads with the real total *for them*,
 * insurance already applied, rather than a headline price they will not pay.
 *
 * Everything the card shows is the answer to a question someone actually asks
 * before booking: what does it cost, when is the soonest, will they come to me,
 * and do I need to fast first.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  Panel,
  Select,
  Spinner,
} from '../../../components/common';
import { useAsync } from '../../../hooks/useAsync';
import { useDebounced } from '../../../hooks/useLocalStorage';
import { serviceApi } from '../../../api/endpoints';
import { formatCurrency } from '../../../domain/pricing';
import {
  SERVICE_CATEGORY_LABELS,
  describeAvailability,
  nextAvailableSlot,
} from '../../../domain/institutions';
import { resolveClinicRgb } from '../../doctor/calendar/legacyTheme';

/** Icon per category, so a surgery and a scan are told apart at a glance. */
const CATEGORY_ICON = {
  surgery: 'scissors',
  lab: 'flask-conical',
  radiology: 'scan',
  inpatient: 'bed',
  icu: 'heart-pulse',
  physiotherapy: 'activity',
};

const SORTS = [
  { id: 'price', label: 'الأرخص أولاً' },
  { id: 'soonest', label: 'الأقرب موعداً' },
  { id: 'name', label: 'الاسم' },
];

export function ServiceCatalog({ tab }) {
  const navigate = useNavigate();

  const state = useAsync(() => serviceApi.catalog(tab.categories), [tab.id], { initialData: [] });
  const services = useMemo(() => state.data || [], [state.data]);

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('price');
  const [category, setCategory] = useState('');
  const debounced = useDebounced(query, 250);

  const accentRgb = useMemo(() => resolveClinicRgb(tab.accent).join(', '), [tab.accent]);

  /**
   * The soonest slot per service, computed once.
   *
   * Without a booking list the browser cannot know what is taken, so this is
   * the *published* calendar rather than live availability — good enough to
   * sort and to say "متاح غداً", and the booking screen checks the real thing
   * against the server before anyone commits.
   */
  const withAvailability = useMemo(
    () =>
      services.map((service) => ({
        ...service,
        next: nextAvailableSlot({ service, bookings: [] }),
      })),
    [services],
  );

  const filtered = useMemo(() => {
    const needle = debounced.trim().toLowerCase();

    let rows = withAvailability;
    if (category) rows = rows.filter((s) => s.category === category);
    if (needle) {
      rows = rows.filter(
        (s) =>
          String(s.name).toLowerCase().includes(needle) ||
          String(s.description || '').toLowerCase().includes(needle) ||
          String(s.institution?.name || '').toLowerCase().includes(needle),
      );
    }

    const sorted = [...rows];
    if (sort === 'price') sorted.sort((a, b) => (a.quote?.total ?? 0) - (b.quote?.total ?? 0));
    else if (sort === 'soonest') {
      // A service with no slot at all sorts last rather than first.
      sorted.sort((a, b) => (a.next?.daysAway ?? 9999) - (b.next?.daysAway ?? 9999));
    } else sorted.sort((a, b) => String(a.name).localeCompare(String(b.name), 'ar'));

    return sorted;
  }, [withAvailability, debounced, sort, category]);

  /** The categories actually present, so the filter never offers an empty one. */
  const presentCategories = useMemo(
    () => [...new Set(services.map((s) => s.category))],
    [services],
  );

  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  if (state.loading && services.length === 0) {
    return <Spinner label={`جاري تحميل ${tab.label}...`} />;
  }

  if (services.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={tab.icon}
          title={`لا توجد ${tab.label} متاحة حالياً`}
          message="لم تقم أي منشأة بإضافة خدمات في هذا القسم بعد. تابعنا قريباً."
        />
      </Panel>
    );
  }

  return (
    <div className="space-y-5" style={{ '--tab-accent': accentRgb }}>
      {/* Filters */}
      <Panel className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ابحث بالاسم أو باسم المستشفى..."
            className="pr-9"
          />
        </div>

        {presentCategories.length > 1 && (
          <Select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            className="sm:w-48"
          >
            <option value="">كل الأنواع</option>
            {presentCategories.map((id) => (
              <option key={id} value={id}>
                {SERVICE_CATEGORY_LABELS[id] || id}
              </option>
            ))}
          </Select>
        )}

        <Select value={sort} onChange={(event) => setSort(event.target.value)} className="sm:w-44">
          {SORTS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Select>
      </Panel>

      <p className="px-1 text-xs text-slate-400">
        {filtered.length} خدمة — الأسعار تشمل خصمك{' '}
        {filtered.some((s) => s.insuranceApplied) && 'وتأمينك الطبي'}
      </p>

      {filtered.length === 0 ? (
        <Panel>
          <EmptyState icon="search-x" title="لا نتائج" message="جرّب كلمة بحث أخرى." />
        </Panel>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {filtered.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
              onBook={() => navigate(`/patient/service/${service.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ========================================================================== */

function ServiceCard({ service, index, onBook }) {
  const quote = service.quote || {};
  const discounted = quote.saving > 0;

  return (
    <Panel
      padded={false}
      className="svc-card flex flex-col rounded-2xl"
      // Staggered so a grid of twelve cards arrives as a wave rather than a jolt.
      style={{ animationDelay: `${Math.min(index, 11) * 45}ms` }}
    >
      <div className="svc-card__bar" aria-hidden="true" />

      <div className="flex flex-1 flex-col gap-4 p-5">
        <div className="flex items-start gap-3">
          <span className="svc-card__icon grid h-11 w-11 shrink-0 place-items-center rounded-xl">
            <Icon name={CATEGORY_ICON[service.category] || 'stethoscope'} size={20} />
          </span>

          <div className="min-w-0 flex-1">
            <h3 className="truncate text-base font-bold text-white">{service.name}</h3>
            <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-slate-400">
              <Icon name="building-2" size={11} className="shrink-0" />
              <span className="truncate">{service.institution?.name || 'منشأة طبية'}</span>
              {service.institution?.verified && (
                <Icon name="check-circle-2" size={11} className="shrink-0 text-emerald-400" />
              )}
            </p>

            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge className="border-white/10 bg-white/5 text-slate-300" icon="clock">
                {describeAvailability(service.next)}
              </Badge>
              {service.estimatedMinutes > 0 && (
                <Badge className="border-white/10 bg-white/5 text-slate-300" icon="hourglass">
                  {service.estimatedMinutes} دقيقة
                </Badge>
              )}
              {service.homeVisitAvailable && (
                <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300" icon="home">
                  زيارة منزلية
                </Badge>
              )}
            </div>
          </div>
        </div>

        {service.description && (
          <p className="line-clamp-2 text-xs leading-relaxed text-slate-400">{service.description}</p>
        )}

        {service.preparationNotes && (
          <p className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/[0.07] p-2.5 text-[11px] leading-relaxed text-amber-200/90">
            <Icon name="alert-circle" size={12} className="mt-0.5 shrink-0" />
            {service.preparationNotes}
          </p>
        )}

        {/* Price — the reason this screen exists */}
        <div className="mt-auto flex flex-wrap items-end justify-between gap-3 border-t border-white/5 pt-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-2">
              {discounted && (
                <span className="text-xs text-slate-500 line-through">
                  {formatCurrency(quote.basePrice)}
                </span>
              )}
              <span className="text-xl font-bold text-emerald-400">
                {formatCurrency(quote.total)}
              </span>
            </div>

            <div className="mt-1 flex flex-wrap gap-1.5">
              {discounted && (
                <span className="svc-save rounded-full px-2 py-0.5 text-[10px] font-black text-white">
                  وفّر {formatCurrency(quote.saving)}
                </span>
              )}
              {service.insuranceApplied && (
                <span className="svc-insured rounded-full px-2 py-0.5 text-[10px] font-black text-white">
                  شامل التأمين
                </span>
              )}
              {quote.deposit?.required && (
                <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-300">
                  عربون {formatCurrency(quote.deposit.amount)}
                </span>
              )}
            </div>
          </div>

          <Button icon="calendar-plus" onClick={onBook}>
            احجز
          </Button>
        </div>
      </div>
    </Panel>
  );
}

export default ServiceCatalog;
